import { useEffect } from "react";

export interface UseClampPageToTotalArgs {
  /** Total row count reported by the backend (0 while nothing has loaded). */
  total: number;
  pageSize: number;
  /** Currently requested page (1-based). */
  page: number;
  /** Called with the last non-empty page when `page` is out of range. */
  onPageChange: (page: number) => void;
}

/**
 * Deleting (or restoring) the last item of the last page leaves the requested
 * page out of range: the backend answers with an empty slice, not an error,
 * so the table would render a misleading empty state. This hook steps back to
 * the last page that still has items.
 *
 * It does nothing while `total` is 0 — both the pre-first-fetch state and a
 * genuinely empty list are valid, not out-of-range.
 */
export function useClampPageToTotal({
  total,
  pageSize,
  page,
  onPageChange,
}: UseClampPageToTotalArgs): void {
  useEffect(() => {
    if (total <= 0 || pageSize <= 0) return;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > lastPage) {
      onPageChange(lastPage);
    }
  }, [total, pageSize, page, onPageChange]);
}
